import axios from '../../../config/axiosConfig';
import { ErrorModal, ExternalLinkRequest, ExternalLinkResponse, ProcessBookmarkReturn } from './HomePage.interfaces';
import { ErrorResponse, ERROR_RESPONSE, IItemRequest, isType, ISubjectGradeInfo } from '../../../shared';
import { BookMarkType } from './HomePage.types';
import { Dispatch } from 'react';
import { loadAllSGInfoSuccess, loadItemCountSuccess, loadSGInfoSuccess } from '../../../redux/queryPanel';
import { loadHTMLPreviewSuccess, loadTabularDataSuccess } from '../../../redux/queryResults';
import { cloneDeep } from 'lodash';
import { EXTERNAL_LINK_RESPONSE, SUBJECT_GRADE_INFO_RESPONSE_PROP, TABULAR_RESPONSE_PROP } from './HomePage.constants';
import { loadPDSelectorsStart } from '../../../redux/explorePanel';

/**
 *
 * @param param URL :bookmarkID
 * @returns boolean
 */
export const isValidBookmarkFormat = (param: string, query: string): boolean => {
  if (!param || !query) return false;
  const bookmark = BookMarkType[param as keyof typeof BookMarkType];
  return bookmark in BookMarkType;
};

export const fetchBookmark = async(request: ExternalLinkRequest): Promise<ExternalLinkResponse|ErrorResponse> => {
  const response = await axios.post<ExternalLinkResponse>('api/externalLink', request)
    .then(res => ({...res.data, __typename: EXTERNAL_LINK_RESPONSE}))
    .catch((errMsg: string) => ({
      __typename: ERROR_RESPONSE,
      errMsg
    } as ErrorResponse));
  return response;
};

/**
 *
 * @param response ExternalLinkResponse
 * @param dispatch useDispatch
 * @returns boolean - If response is the expected shape, actions are dispatched and True is returned. Else return false
 */
export const processValidBookmark = (response: ExternalLinkResponse, dispatch: Dispatch<unknown>): boolean => {
  // unselect all to cache SGInfo
  if (
    (SUBJECT_GRADE_INFO_RESPONSE_PROP in response) &&
    (TABULAR_RESPONSE_PROP in response)
  ) {
    const cachedSGInfo = response.subjectGradeInfoResponse.subjectGradeInfo.map((sgInfo): ISubjectGradeInfo => ({
      ...sgInfo,
      isSelected: false,
      gradeList: sgInfo.gradeList.map( gd => ({
        ...gd,
        isSelected: false
      }))
    }));


    dispatch(
      loadAllSGInfoSuccess({
        subjectCode: '',
        gradeStr: '',
        systemID: '',
        subjectGradeInfo: cachedSGInfo,
        yearsInfo: [],
        contentClassifications: {
          contentClassificationList: null
        },
        itemTypes: [],
        difficultyInfo: []
      })
    );
    dispatch(
      loadItemCountSuccess({itemCount: response.tabularResponse.itemCount})
    );
    dispatch(
      loadSGInfoSuccess(response.subjectGradeInfoResponse)
    );

    const subGradInfo = response.subjectGradeInfoResponse.subjectGradeInfo.find(sub => sub.isSelected);

    // edge case: if no years selected, select all
    const yearsInfo = cloneDeep(response.subjectGradeInfoResponse.yearsInfo);
    if (!yearsInfo.filter(yi => yi.isSelected).length) {
      yearsInfo.forEach( yi => (yi.isSelected = true));
    }

    // edge case: if all children selected, select parents
    const contentClassificationsList = cloneDeep(response.subjectGradeInfoResponse.contentClassifications?.contentClassificationList);
    if (contentClassificationsList) {
      contentClassificationsList.forEach(cc => {
        let isAllSelected = true;
        cc.childList.forEach(child => {
          if (!child.isSelected) {
            isAllSelected = false;
          }
        });
        cc.isSelected = isAllSelected;
      });
    }

    const itemRequest: IItemRequest = {
      SubjectCode: response.subjectGradeInfoResponse.subjectCode,
      GradeStr: response.subjectGradeInfoResponse.gradeStr,
      SystemID: response.subjectGradeInfoResponse.systemID,
      SubjectGradeInfo: subGradInfo ? [subGradInfo] : undefined,
      YearsInfo: yearsInfo,
      LimitToOnlineItems: false,
      ContentClassifications: {
        contentClassificationList: contentClassificationsList ?
          contentClassificationsList :
          null
      },
      ItemTypes: response.subjectGradeInfoResponse.itemTypes,
      DifficultyInfo: response.subjectGradeInfoResponse.difficultyInfo,
      BookmarkedTab: response.defTab

    };

    dispatch(loadPDSelectorsStart(
      itemRequest
    ));
    dispatch(loadTabularDataSuccess({
      itemRequest,
      ...response.tabularResponse
    }));
    dispatch(loadHTMLPreviewSuccess(
      response.htmlPreviewResponse
    ));

    return true;
  } else {

    return false;
  }
};

/**
 *
 * @param param URL :bookmarkID
 * @param query ?[any_param_after_question_mark]
 * @param setModalError If set, generic error modal is opened
 * @param setIsLoading Sets spinner
 * @param dispatch useDispatch - dispatch actions if validation successful
 * @returns boolean - if True -> navigate to searchQuestions from view; else stay home
 */
export const processBookmark = async(
  param: BookMarkType,
  query: string,
  setModalError: React.Dispatch<React.SetStateAction<ErrorModal>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  dispatch: Dispatch<unknown>
): Promise<ProcessBookmarkReturn> => {
  const request = {
    bookMarkType: param,
    items: query
  } as ExternalLinkRequest;

  let flag = false;
  let defTab = '';
  setIsLoading(true);

  const response = await fetchBookmark(request);

  if (
    isType<ExternalLinkResponse>(response, EXTERNAL_LINK_RESPONSE) &&
    processValidBookmark(response, dispatch)
  ) {

    flag = true;
    // if query contains deftab= then set defaultTab to its value
    if (query.includes('deftab=')) {
      const qryParts = query.split('&');
      qryParts.forEach(element => {
        element.includes('deftab=') && (defTab = element.split('=')[1]);
      });
    }
  } else if (isType<ErrorResponse>(response, ERROR_RESPONSE)) {
    console.log('ERROR');
    setModalError({
      headerText: 'Invalid Link',
      bodyText: response.errMsg
    });
  }

  setIsLoading(false);

  return {flag, defTab};
};